// src/components/ProductCategories/ProductCategoryPage.js
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './category.css';

// Images for disposable medical wear collection
import surgicalGownImg from '../../images/disposable-surgical-gowns.png';
import hipudrapeImg from '../../images/u-cut-Closeup.png';
import kneeodrapeImg from '../../images/knee-o-drape-three.jpg';
import arthroscopyDrapeImg from '../../images/arthoscopy-drape-four.jpg';
import gynaeDrapeImg from '../../images/pdpimage/gynaethree.jpg';
import generalOTDrapeImg from '../../images/general-ot-one.jpg';

// Placeholder imports for orthopedic consumables (replace with actual images)
import abdomenSpineSupportImg from '../../images/Abdomen&SpineSupport.png';
import cervicalSupportAidsImg from '../../images/CervicalSupportAids.png';
import shoulderClavicleSupportImg from '../../images/Shoulder&Clavicle Support.png';
import armElbowSupportImg from '../../images/Arm&ElbowSupport.png';
import wristFingersThumbSupportImg from '../../images/WristFingers&ThumbSupport.png';
import pelvicHipSupportImg from '../../images/Pelvic&HipSupport.png';
import thighKneeAnkleCalfSupportImg from '../../images/ThighKneeAnkle&CalfSupport.png';
import footHeelToesSupportImg from '../../images/Foot,Heel&ToesSupport.png';

// surgical consumables product images
import pulselavage from '../../images/pulso-main-image.png'
import SagittalBlade from '../../images/Sagittal Blade.jpg'
import OrthopedicSuctionTube from '../../images/Orthopedic-Suction-tube.png'
import ArthroscopicShaverBlade from '../../images/ArthroscopicShaver-Blade.png'
// import AdhesiveIncisDrape from '../../images/pulso-main-image.png'

// Map URL slug to category product key
const PATH_TO_CATEGORY = {
  "disposable-medical-wear-collection": "/products/disposable-medical-wear-collection",
  "surgical-consumables": "/products/surgical-consumables",
  "orthopedic-implants": "/products/orthopedic-implants",
  "orthopedic-consumables": "/products/orthopedic-consumables",
};

// Product data keyed by category path
const CATEGORY_PRODUCTS = {
  "/products/disposable-medical-wear-collection": [
    {
      name: "Surgical Gown",
      subtitle: "Sterile, reinforced protection for surgical teams.",
      link: "/surgical-gown",
      image: surgicalGownImg,
      description:
        " Designed with SMS/SMMS fabric, our surgical gowns ensure breathability and barrier performance. Available in standard, reinforced, and wraparound variants.",
    },
    {
      name: "Orthopedic Hip U Drape",
      subtitle: "Targeted coverage for hip arthroplasty procedures.",
      link: "/Hip-u-drape",
      image: hipudrapeImg,
      description:
        " U-shaped fenestration and fluid control pouches ensure safe and efficient draping for joint surgeries.",
    },
    {
      name: "Knee O Drape",
      subtitle: "Precision-fit draping for knee replacement and ortho cases.",
      link: "/knee-o-drape",
      image: kneeodrapeImg,
      description:
        " Circular fenestration and absorbent reinforcements offer clean surgical access with superior fluid containment.",
    },
    {
      name: "Arthroscopy Drape",
      subtitle: "Minimal obstruction, maximum fluid control. ",
      link: "/Arthroscopy-drape",
      image: arthroscopyDrapeImg,
      description:
        "Transparent film and suction zones help maintain visibility and manage irrigation fluids during arthroscopic procedures.",
    },
    {
      name: "Gynae Drape",
      subtitle: "Ergonomic, privacy-focused design for OBGYN procedures.",
      link: "/Gynae-drape",
      image: gynaeDrapeImg,
      description:
        " Cutout windows and absorbent zones enable focused access while maintaining coverage and hygiene.",
    },
    {
      name: "General OT Drape",
      subtitle: "All-purpose draping for general surgeries.",
      link: "/general-ot-drape",
      image: generalOTDrapeImg,
      description:
        "Flexible design with sterile borders and fluid repellent layers for diverse procedural use.",
    },
  ],

  "/products/surgical-consumables": [
    {
      name: "Pulse Lavage",
      subtitle: "Targeted irrigation. Enhanced wound care.",
      link: "https://pulsojet.sabiote.in/",
      image: pulselavage,
      description:
        "Our sterile, battery-operated Pulse Lavage system is engineered for orthopedic debridement and trauma care. It provides controlled irrigation to remove debris, bacteria, and necrotic tissue—minimizing infection risk and improving surgical outcomes.",
    },
    {
      name: "Sagittal Blade",
      subtitle: "Precision bone cutting with optimal control.",
      link: "/sagittal-blade",
      image: SagittalBlade,
      description:
        "Single-use sagittal saw blades made from hardened stainless steel for high-performance cutting during orthopedic procedures. Available in various lengths and tooth configurations for diverse surgical needs.",
    },
    {
      name: "Orthopedic Suction Tube",
      subtitle: "Dual-filtration for clean suction.",
      link: "/orthopedic-suction-tube",
      image: OrthopedicSuctionTube,
      description:
        "Designed specifically for orthopedic surgeries, our suction tubes come with integrated dual filters that reduce fluid contamination and ensure uninterrupted suction flow. Compatible with standard suction systems.",
    },
    {
      name: "Arthroscopic Shaver Blade",
      subtitle: "Sharp, durable, and vibration-free.",
      link: "/arthroscopy-shaver-blades",
      image: ArthroscopicShaverBlade,
      description:
        "Compatible with leading arthroscopy systems, our single-use shaver blades offer precise tissue resection during minimally invasive procedures. Engineered for balance, torque efficiency, and minimal chatter.",
    },
    {
      name: "Adhesive Incise Drape",
      subtitle: "Secure incision zone. Bacterial barrier.",
      link: "#",
      image: "",
      description:
        "Transparent, breathable, and self-adhesive film drapes that create a sterile field directly over the surgical site. Designed to prevent microbial migration and enhance adhesion to the skin, even in moist environments.",
    },
  ],

  "/products/orthopedic-implants": [
    ...Array(6).fill({
      name: "Coming Soon",
      link: "#",
      image: "",
      description: "New product details will be updated soon. Stay tuned!",
    }),
  ],

  "/products/orthopedic-consumables": [
    {
      name: "Abdomen & Spine Support",
      subtitle: "Postural alignment and core stability.",
      link: "#",
      image: abdomenSpineSupportImg,
      description:
        "Elastic and reinforced belts designed for lower back pain, post-operative care, and spinal alignment. Supports include lumbar belts, abdominal binders, and spinal orthoses for postural control.",
    },
    {
      name: "Cervical Support Aids",
      subtitle: "Neck immobilization made comfortable.",
      link: "#",
      image: cervicalSupportAidsImg,
      description:
        "A range of soft and rigid cervical collars offering firm yet comfortable neck stabilization for injuries, post-surgery care, or spondylitis relief. Contoured for optimal chin and neck support.",
    },
    {
      name: "Shoulder & Clavicle Support",
      subtitle: "Restore shoulder posture and mobility.",
      link: "#",
      image: shoulderClavicleSupportImg,
      description:
        "Immobilizers and clavicle braces that aid recovery from dislocations, fractures, and shoulder instability. Lightweight and adjustable for targeted upper body support.",
    },
    {
      name: "Arm & Elbow Support",
      subtitle: "Secure forearm and elbow recovery.",
      link: "#",
      image: armElbowSupportImg,
      description:
        "Tennis elbow supports, compression sleeves, and arm slings designed to relieve stress on the elbow joint and stabilize arm movement after injury or surgery.",
    },
    {
      name: "Wrist, Fingers & Thumb Support",
      subtitle: "Grip stability, pain relief, and precision.",
      link: "#",
      image: wristFingersThumbSupportImg,
      description:
        "Splints and braces for wrist strain, carpal tunnel, thumb immobilization, and finger injuries. Made with breathable, low-profile materials for extended wear comfort.",
    },
    {
      name: "Pelvic & Hip Support",
      subtitle: "Pelvic stability and targeted relief.",
      link: "#",
      image: pelvicHipSupportImg,
      description:
        "Support belts and orthoses designed for sacroiliac joint dysfunction, pelvic instability, and post-pregnancy care. Ergonomically designed to align the hips and relieve pressure.",
    },
    {
      name: "Thigh, Knee, Ankle & Calf Support",
      subtitle: "Lower limb support from thigh to toe.",
      link: "#",
      image: thighKneeAnkleCalfSupportImg,
      description:
        "A full range including knee braces, patella supports, thigh sleeves, ankle stabilizers, and calf supports. Engineered for joint protection and muscle recovery in high-mobility zones.",
    },
    {
      name: "Foot, Heel & Toes Support",
      subtitle: "Targeted relief for every step.",
      link: "#",
      image: footHeelToesSupportImg,
      description:
        "Heel cups, toe separators, foot drop splints, and arch supports designed to reduce pressure, improve gait, and promote recovery in foot-related conditions.",
    },
  ],
};

// Banner content
const CATEGORY_BANNER_CONTENT = {
  "/products/disposable-medical-wear-collection": {
    title: "Explore Our Protective Medical Wear Solutions",
    subtitle: "Trusted barrier protection for surgical and isolation environments.",
  },
  "/products/surgical-consumables": {
    title: "Explore Our High-Performance Surgical Consumables",
    subtitle: "Designed for precision, safety, and infection control in demanding clinical environments.",
  },
  "/products/orthopedic-implants": {
    title: "Orthopedic Implants & Instruments",
    subtitle: "Reliable fixation, precision performance for orthopedic surgery.",
  },
  "/products/orthopedic-consumables": {
    title: "Supportive Orthopedic Solutions for Everyday Mobility",
    subtitle: "Therapeutic braces and supports designed to stabilize, protect, and enhance recovery across every body zone.",
  },
};

// Intro paragraphs per category
const CATEGORY_INTRO_PARAGRAPH = {
  "/products/disposable-medical-wear-collection":
    "At Sabiote Healthcare, we design and manufacture a comprehensive line of disposable medical wear that meets the stringent demands of modern clinical settings. Whether it’s for routine procedures or high-risk surgeries, our garments are engineered to deliver comfort, compliance, and contamination control. Certified to CE and ISO standards, our offerings support infection prevention efforts across operating rooms, ICUs, and outpatient facilities.",
  "/products/surgical-consumables":
    "At Sabiote Healthcare, we manufacture a focused range of surgical consumables tailored to the needs of orthopedic and minimally invasive procedures. From wound irrigation to bone resection, our products enhance surgical efficiency while ensuring safety and compatibility with modern operating protocols. Backed by MDR & ISO standards, each offering is optimized for sterile use, reliability, and performance.",
  "/products/orthopedic-consumables":
    "Sabiote Healthcare offers a complete line of orthopedic support products developed to meet the needs of injury prevention, post-surgical care, and chronic musculoskeletal conditions. Our orthopedic consumables provide ergonomic design, anatomical fit, and targeted compression — empowering users with mobility, comfort, and healing support. Whether for clinical use or everyday rehabilitation, each product is crafted using skin-friendly materials and robust fastening systems for reliable performance.",
};

// CTA Banner
const ContactBanner = () => (
  <div className="contact-banner">
    <h3>Looking for Product Catalogues or Bulk Procurement Details?</h3>
    <Link to="/contact" className="homepage-btn-primary">Contact Sales Team</Link>
  </div>
);

const ProductCategoryPage = () => {
  const { categorySlug } = useParams();
  const categoryPath = PATH_TO_CATEGORY[categorySlug];

  if (!categoryPath) {
    return <div style={{ padding: "2rem" }}><h2>Category not found</h2></div>;
  }

  const products = CATEGORY_PRODUCTS[categoryPath] || [];

  // Set total cards based on category
  let totalCards = 6; // default
  if (categoryPath === "/products/surgical-consumables") {
    totalCards = 5; // 5 cards for surgical consumables, no placeholders
  } else if (categoryPath === "/products/orthopedic-consumables") {
    totalCards = 8; // 8 cards for orthopedic consumables, no placeholders
  }

  let displayProducts = products;

  // Add placeholders only for disposable-medical-wear-collection and other categories (not surgical or orthopedic consumables)
  if (
    categoryPath !== "/products/surgical-consumables" &&
    categoryPath !== "/products/orthopedic-consumables"
  ) {
    const placeholdersNeeded = totalCards - products.length;
    const placeholderCards = Array.from({ length: placeholdersNeeded > 0 ? placeholdersNeeded : 0 }).map(() => ({
      name: "Coming Soon",
      link: "#",
      image: "",
      description: "New product details will be updated soon. Stay tuned!",
    }));
    displayProducts = [...products, ...placeholderCards].slice(0, totalCards);
  } else {
    displayProducts = products.slice(0, totalCards);
  }

  const bannerContent = CATEGORY_BANNER_CONTENT[categoryPath] || {
    title: "Products",
    subtitle: "",
  };

  const introParagraph = CATEGORY_INTRO_PARAGRAPH[categoryPath] || "";

  // Function to decide if a product card should be clickable (i.e., use Link) or just a div (non-clickable)
  const isClickable = (product) => {
    if (categoryPath === "/products/disposable-medical-wear-collection") {
      // All clickable
      return true;
    }
    if (categoryPath === "/products/surgical-consumables") {
      // Only Pulse Lavage clickable
      // return product.name === "Pulse Lavage";
      return true;
    }
    // For orthopedic implants and orthopedic consumables no clickable
    if (
      categoryPath === "/products/orthopedic-implants" ||
      categoryPath === "/products/orthopedic-consumables"
    ) {
      return false;
    }
    return false;
  };

  return (
    <>
      <Helmet>
        <title>Built by Us, Trusted by Surgeons</title>
      </Helmet>

      {/* Hero Section */}
      <div className="hero-ban">
        <div className="products-hero-banner">
          <section className="homepage-hero">
            <h1 className="homepage-hero-title">{bannerContent.title}</h1>
            <p className="homepage-hero-subtext">{bannerContent.subtitle}</p>
            <div className="homepage-hero-buttons">
              <a href="#products" className="homepage-btn-outline">View Products</a>
            </div>
          </section>
        </div>
      </div>

      {/* Product Grid */}
      <div className="mw-category-list" id="products">
        <h2 className="homepage-section-title">Product Segments We Offer</h2>
        <p className="mw-subtext">{introParagraph}</p>

        <div className="product-grid">
          {displayProducts.map((product, index) => {
            const clickable = isClickable(product);

            const productContent = (
              <>
                <div className="card-image">
                  {product.image ? (
                    <img src={product.image} alt={product.name} loading="lazy" />
                  ) : (
                    <div
                      style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        color: '#666',
                        fontStyle: 'italic',
                        border: '1px dashed #ccc',
                        height: '180px',
                      }}
                      aria-label="Image coming soon"
                    >
                      Image coming soon
                    </div>
                  )}
                </div>
                <div className="card-title-numbered">
                  <span>{index + 1}. </span>
                  <span className="card-title">{product.name}</span>
                </div>
                {product.subtitle && (
                  <div className="card-subtitle">{product.subtitle}</div>
                )}
                <p className="card-description">{product.description}</p>
              </>
            );

            return clickable ? (
              <Link to={product.link} key={index} className="product-card clickable">
                {productContent}
              </Link>
            ) : (
              <div
                key={index}
                className="product-card non-clickable"
                aria-disabled="true"
                style={{ cursor: 'default' }}
              >
                {productContent}
              </div>
            );
          })}
        </div>
      </div>

      <ContactBanner />
    </>
  );
};

export default ProductCategoryPage;
